/**
 * Input validation system with localized error messages
 * Manages errors with internationalization support
 */

import type { FurnitureData } from "@/components/calculator/FurnitureInputForm";
import { i18nManager } from "./i18n";

export interface ValidationError {
  field: keyof FurnitureData;
  message: string;
  code:
    | "INVALID_NUMBER"
    | "NEGATIVE_VALUE"
    | "MAX_VALUE_EXCEEDED"
    | "REQUIRED_FIELD";
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

/**
 * Validation constants
 */
export const VALIDATION_LIMITS = {
  MAX_OFFICE_AREA: 50000, // maximum m²
  MAX_FURNITURE_COUNT: 10000, // maximum number of furniture units
  MIN_VALUE: 0, // minimum value
} as const;

/**
 * Furniture data validation class
 */
export class FurnitureValidator {
  /**
   * Validates an individual field
   */
  validateField(
    field: keyof FurnitureData,
    value: string | number,
  ): ValidationError | null {
    // Convert string to number if needed
    const numericValue =
      typeof value === "string" ? this.sanitizeValue(value) : value;

    // Check numeric type
    if (typeof numericValue !== "number" || Number.isNaN(numericValue)) {
      return {
        field,
        message: i18nManager.getTranslation("validation.invalidNumber"),
        code: "INVALID_NUMBER",
      };
    }

    // Check negative values
    if (numericValue < VALIDATION_LIMITS.MIN_VALUE) {
      return {
        field,
        message: i18nManager.getTranslation("validation.negativeValue"),
        code: "NEGATIVE_VALUE",
      };
    }

    // Check maximum limits
    const maxValue =
      field === "officeArea"
        ? VALIDATION_LIMITS.MAX_OFFICE_AREA
        : VALIDATION_LIMITS.MAX_FURNITURE_COUNT;

    if (numericValue > maxValue) {
      return {
        field,
        message: i18nManager.getTranslation("validation.maxValueExceeded"),
        code: "MAX_VALUE_EXCEEDED",
      };
    }

    return null;
  }

  /**
   * Validates all furniture data
   */
  validate(furnitureData: FurnitureData): ValidationResult {
    const errors: ValidationError[] = [];

    // Validate each field
    Object.entries(furnitureData).forEach(([key, value]) => {
      const error = this.validateField(key as keyof FurnitureData, value);
      if (error) {
        errors.push(error);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validates and cleans an input value
   */
  sanitizeValue(value: string | number): number {
    if (typeof value === "string") {
      // Clean string: remove spaces and non-numeric characters
      const cleaned = value.replace(/[^\d.,]/g, "").replace(",", ".");
      const parsed = parseFloat(cleaned);
      return Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
    }

    if (typeof value === "number") {
      return Number.isNaN(value) ? 0 : Math.max(0, value);
    }

    return 0;
  }

  /**
   * Checks if data is sufficient for calculation
   */
  hasMinimumData(furnitureData: FurnitureData): boolean {
    const totalFurniture =
      furnitureData.cabinets +
      furnitureData.individualDesks +
      furnitureData.officeChairs +
      furnitureData.canteenTables +
      furnitureData.meetingTables +
      furnitureData.visitorChairs;

    return (
      furnitureData.fte > 0 ||
      furnitureData.officeArea > 0 ||
      totalFurniture > 0
    );
  }

  /**
   * Gets a localized error message for an error code
   */
  getLocalizedErrorMessage(code: ValidationError["code"]): string {
    const messageKeys = {
      INVALID_NUMBER: "validation.invalidNumber",
      NEGATIVE_VALUE: "validation.negativeValue",
      MAX_VALUE_EXCEEDED: "validation.maxValueExceeded",
      REQUIRED_FIELD: "validation.requiredField",
    };

    return i18nManager.getTranslation(messageKeys[code]);
  }
}

// Singleton validator instance
export const furnitureValidator = new FurnitureValidator();

/**
 * Utility function for real-time input validation
 */
export const validateInput = (
  field: keyof FurnitureData,
  value: string,
): {
  isValid: boolean;
  sanitizedValue: number;
  error?: ValidationError;
} => {
  const sanitizedValue = furnitureValidator.sanitizeValue(value);
  const error = furnitureValidator.validateField(field, sanitizedValue);

  return {
    isValid: error === null,
    sanitizedValue,
    error: error || undefined,
  };
};

/**
 * Custom hook for real-time validation (to use with React)
 */
export const useValidation = () => {
  return {
    validate: furnitureValidator.validate.bind(furnitureValidator),
    validateField: furnitureValidator.validateField.bind(furnitureValidator),
    sanitizeValue: furnitureValidator.sanitizeValue.bind(furnitureValidator),
    hasMinimumData: furnitureValidator.hasMinimumData.bind(furnitureValidator),
    getLocalizedErrorMessage:
      furnitureValidator.getLocalizedErrorMessage.bind(furnitureValidator),
  };
};
